import { useAuthStore } from '../store/auth';
import { canSeePermission, type RequiredPermission } from '../registry/access';

/**
 * 当前用户是否持有权限码（未加载 RBAC 上下文时返回 false）。
 * 传数组表示「任一命中」；不传或空数组表示无权限要求（恒为 true）。
 * admin 恒放行：后端解析 RBAC 上下文时对 admin 返回全量权限码，前端不重复查表。
 */
export function usePerm(code?: string | string[]): boolean {
  const rbac = useAuthStore((s) => s.rbac);
  const role = useAuthStore((s) => s.user?.role);
  const codes = Array.isArray(code) ? code : code ? [code] : [];
  if (codes.length === 0) return true;
  if (role === 'admin') return true; // 兼容：admin 恒全量
  if (!rbac) return false;
  return codes.some((c) => rbac.permissions.includes(c));
}

/** 当前用户是否持有任一权限码 */
export function useAnyPerm(codes: string[]): boolean {
  const rbac = useAuthStore((s) => s.rbac);
  const role = useAuthStore((s) => s.user?.role);
  if (role === 'admin') return true;
  if (!rbac) return false;
  return codes.some((c) => rbac.permissions.includes(c));
}

/**
 * 权限码判定 + 「权限集是否已就绪」二件套。
 *
 * 判据与 `usePerm` 同源（admin 恒放行、其余按 rbac 权限码命中），只多给一个 `ready`：
 * `ready=false` 表示 `/auth/me` 还没回来，此刻的 `allowed=false` **不代表用户确实没有该能力**。
 * 需要把结论写成断定性文案的地方（「需额外授予：…」这类）必须走本 hook：只按 `allowed` 渲染，
 * 就会先把话说死、权限集到手后再撤掉，用户看到的是可见的闪动与误导。
 * 按钮/入口本身可以先 fail-closed 收着（`allowed=false` 即禁用），但文案要等 `ready`。
 */
export function usePermReady(code: string): { allowed: boolean; ready: boolean } {
  const rbac = useAuthStore((s) => s.rbac);
  const role = useAuthStore((s) => s.user?.role);
  return {
    allowed: canSeePermission(code, role, rbac?.permissions ?? []),
    // admin 恒全量，不必等权限集；其余角色以 rbac 非空为「已按真实权限渲染」的判据
    ready: role === 'admin' || !!rbac,
  };
}

/**
 * 读取当前用户权限码集合（供非组件场景使用：菜单裁剪等纯函数入口）。
 * RBAC 上下文未加载时返回空数组——调用方须保留角色兜底，避免入口瞬时消失。
 */
export function currentPermissions(): string[] {
  return useAuthStore.getState().rbac?.permissions || [];
}

/**
 * 可见性判定器：`canSee(权限码 | 码组)`，判据与侧栏/功能地图/路由守卫同源（`registry/access.ts`）。
 * 用于「页内 Tab 按各自权限码裁剪」这类场景——Tab 是数组，逐个调 `usePerm` 会违反 hooks 数量稳定性。
 * 注意：RBAC 上下文未加载时非 admin 用户一律不可见（与 usePerm 同口径），
 * 因此不能在未加载时放行，否则会先渲染出无权访问的 Tab 并打出 403 请求。
 */
export function useCanSee(): (perm: RequiredPermission) => boolean {
  const rbac = useAuthStore((s) => s.rbac);
  const role = useAuthStore((s) => s.user?.role);
  return (perm: RequiredPermission) => canSeePermission(perm, role, rbac?.permissions ?? []);
}

